<template>
  <button class="pe-btn-general" :class="[
    `pe-btn-general--${variant}`,
    configClass,                 // Clase inyectada desde la config global
    { 'pe-btn-general--loading': loading }
  ]" :disabled="disabled || loading">
    <span v-if="loading" class="pe-btn-general-loader">
      <svg class="pe-spinner" viewBox="0 0 50 50">
        <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
      </svg>
      <span v-if="loadingText" class="pe-loading-text">{{ loadingText }}</span>
    </span>

    <span v-else class="pe-btn-general-content">
      <slot></slot>
    </span>
  </button>
</template>

<script setup>
import { computed, inject } from 'vue';

// No necesitamos declarar 'click', 'type', etc. Vue los pasa directo al <button>
const props = defineProps({
  loading: { type: Boolean, default: false },
  loadingText: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  variant: {
    type: String,
    default: 'primary',
    validator: (val) => ['primary', 'secondary', 'submit', 'prev', 'next', 'danger'].includes(val)
  }
});

const globalConfig = inject('engineConfig', {});

// Mapeo dinámico: Según la variante, buscamos la clase en la config global
const configClass = computed(() => {
  if (!globalConfig?.classes) return '';

  switch (props.variant) {
    case 'submit': return globalConfig.classes.submitBtn;
    case 'prev': return globalConfig.classes.prevBtn;
    case 'next': return globalConfig.classes.nextBtn;
    default: return ''; // Primary/Secondary usan estilos default de la librería
  }
});
</script>

<style scoped>
/* ESTILOS BASE */
.pe-btn-general {
  border: var(--pe-btn-general-border, none);
  border-radius: var(--pe-btn-general-b-rd, 0.5rem);
  padding: var(--pe-btn-general-pd, 1vw 2vw);
  gap: var(--pe-btn-general-gp, auto);
  transition: filter 0.2s, transform 0.15s, opacity 0.2s;
  cursor: pointer;
}

.pe-btn-general--primary,
.pe-btn-general--submit,
.pe-btn-general--next,
.pe-btn-general--prev {
  background: var(--pe-btn-primary-bg, var(--pe-btn-general-bg, black));
  color: var(--pe-btn-primary-color, var(--pe-btn-general-color, white));
}

.pe-btn-general--secondary {
  background: var(--pe-btn-secondary-bg, var(--pe-drop-zone-bg, #eeeeee41));
  color: var(--pe-btn-secondary-color, var(--pe-input-text-color, #333333));
}

.pe-btn-general--danger {
  background: var(--pe-btn-danger-bg, #d32f2f);
  color: var(--pe-btn-danger-color, white);
}

.pe-btn-general:disabled {
  background: var(--pe-btn-general-disabled-bg, grey);
}

.pe-btn-general:not(:disabled):hover {
  filter: brightness(0.9);
  /* Oscurece un poco al hover genérico */
  transform: translateY(-1px);
}

.pe-btn-general:active {
  transform: translateY(0);
}

/* LOADER CSS */
.pe-btn-general-loader {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pe-spinner {
  animation: rotate 2s linear infinite;
  width: 1.2em;
  height: 1.2em;
}

.pe-spinner .path {
  stroke: currentColor;
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }

  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }

  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

.pe-btn-general-content {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
</style>